import { useEffect, useMemo, useState } from 'react';
import { Activity, trackers } from '../api';
import { dateTime, dayLabel, irishDate, irishHour, shiftDate } from '../format';

const HOURS = Array.from({ length: 24 }, (_, h) => h);

// Days × hours: when the tracker recorded data, and whether it reached us on time.
// Late = buffered on the tracker (recorded long before the server received it).
export function ActivityGrid({ trackerId }: { trackerId: number }) {
  const today = irishDate();
  const [range, setRange] = useState({ from: shiftDate(today, -9), to: today });
  const [data, setData] = useState<Activity | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!range.from || !range.to) return;
    setError('');
    trackers.activity(trackerId, range.from, range.to).then(setData).catch((e) => setError(e.message));
  }, [trackerId, range]);

  const { days, cells, power } = useMemo(() => {
    const days: string[] = [];
    if (data) for (let d = data.to; d >= data.from; d = shiftDate(d, -1)) days.push(d);
    const cells = new Map(data?.cells.map((c) => [`${c.d} ${c.h}`, c]));
    const power = new Map<string, Array<'lost' | 'restored'>>();
    for (const p of data?.power ?? []) {
      const key = `${irishDate(new Date(p.t))} ${irishHour(p.t)}`;
      power.set(key, [...(power.get(key) ?? []), p.kind]);
    }
    return { days, cells, power };
  }, [data]);

  return (
    <section className="card form" aria-label="Data activity">
      <div className="page-head">
        <h2>Data activity</h2>
        <div className="range">
          <label className="sr-only" htmlFor="act-from">From</label>
          <input id="act-from" type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          <span aria-hidden="true">–</span>
          <label className="sr-only" htmlFor="act-to">To</label>
          <input id="act-to" type="date" value={range.to} min={range.from} max={today} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </div>
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {data && (
        <div className="agrid" role="table" aria-label="Records per hour, Irish time">
          <div className="agrid-row agrid-hours" role="row">
            <span role="columnheader" className="sr-only">Day</span>
            {HOURS.map((h) => <span key={h} role="columnheader">{h % 3 === 0 ? String(h).padStart(2, '0') : ''}</span>)}
          </div>
          {days.map((d) => (
            <div className="agrid-row" role="row" key={d}>
              <span role="rowheader" className="agrid-day">{dayLabel(`${d}T12:00:00Z`)}</span>
              {HOURS.map((h) => {
                const c = cells.get(`${d} ${h}`);
                const marks = power.get(`${d} ${h}`) ?? [];
                const state = !c ? 'none' : c.late === c.n ? 'late' : 'ok';
                const label = `${dayLabel(`${d}T12:00:00Z`)} ${String(h).padStart(2, '0')}:00 – ${!c ? 'no data' : `${c.n} records${c.late ? `, ${c.late} late` : ''}`}`
                  + (marks.length ? `; power ${marks.join(', then ')}` : '');
                return (
                  <span key={h} role="cell" className={`acell acell-${state}`} title={label} aria-label={label}>
                    {marks.length > 0 && <i className={`amark amark-${marks[marks.length - 1]}`} />}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      )}
      <ul className="legend small">
        <li><i className="acell acell-ok" /> On time</li>
        <li><i className="acell acell-late" /> Late (buffered over {data?.lateAfterMin ?? 5} min)</li>
        <li><i className="acell acell-none" /> No data</li>
        <li><i className="amark amark-lost" /> Power lost</li>
        <li><i className="amark amark-restored" /> Power restored</li>
      </ul>
      {data?.power.length ? (
        <details>
          <summary className="small">Power changes ({data.power.length})</summary>
          <ul className="small">
            {[...data.power].reverse().slice(0, 50).map((p) => <li key={p.t}>{dateTime(p.t)}: {p.kind === 'lost' ? 'power lost' : 'power restored'}</li>)}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
